import { useAtomValue } from "@effect/atom-react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftRightIcon,
  Building2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsUpDownIcon,
  PaletteIcon,
  SearchIcon,
  UserPlusIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { openCommandPalette } from "../commandPaletteBus";
import { primaryServerKeybindingsAtom } from "../state/server";
import { resolveShortcutCommand, shortcutLabelForCommand } from "../keybindings";
import { isTerminalFocused } from "../lib/terminalFocus";
import { cn } from "../lib/utils";
import { orgUrlSegment } from "../orgRouting";
import { useActiveOrgId } from "../state/orgs";
import { OrgLogo, OrgMenuItems, useOrgSelection } from "./OrgSwitcher";
import { AmoregMark } from "./sidebar/SidebarChrome";
import { AppBreadcrumb } from "./apps/AppBreadcrumb";
import { WorkspaceUserMenu } from "./auth/WorkspaceUserMenu";
import { AskAiPanel } from "./guide/AskAiPanel";
import { Button } from "./ui/button";
import { Kbd } from "./ui/kbd";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from "./ui/menu";

/** "Amoreg / <company> / <app>": where you are, and the switch to be somewhere else. */
/** Roomy rows, as in the reference: the menu is a place to read, not a toolbar. */
const COMPANY_MENU_ROW = "h-9 min-h-9 cursor-pointer gap-2.5 rounded-lg px-2.5 text-[13px]";
const COMPANY_MENU_ICON = "size-4 shrink-0 text-muted-foreground";

function ShellBreadcrumb() {
  const { orgs, activeOrg, available, selectOrg } = useOrgSelection();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"menu" | "companies">("menu");
  const navigate = useNavigate();
  const goToSettings = (section: "organization" | "members" | "branding") => {
    if (!activeOrg) return;
    void navigate({
      to: "/$org/settings/$section",
      params: { org: orgUrlSegment(activeOrg.id), section },
    });
  };
  return (
    <div className="pointer-events-auto flex min-w-0 items-center gap-1.5 [-webkit-app-region:no-drag]">
      <Link
        to="/"
        aria-label="Amoreg home"
        className="flex h-7 shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-1.5 text-sidebar-foreground outline-hidden ring-ring focus-visible:ring-2"
      >
        <AmoregMark />
        <span className="text-[13px] font-semibold tracking-tight">Amoreg</span>
      </Link>
      {available ? (
        <>
          <span aria-hidden className="select-none text-base font-light text-sidebar-muted-foreground/60">
            /
          </span>
          <Menu
            open={open}
            onOpenChange={(next) => {
              setOpen(next);
              // Always reopen on the menu, never halfway into the company list.
              if (next) setView("menu");
            }}
          >
            <MenuTrigger
              aria-label={
                activeOrg ? `Company menu — ${activeOrg.name}` : "Choose a company"
              }
              data-active={open || undefined}
              // A tint of the text colour, like every other hover in the chrome:
              // it reads on the window surface in light and dark alike.
              className="flex h-7 min-w-0 cursor-pointer items-center gap-2 rounded-md px-1.5 text-[13px] font-medium text-sidebar-foreground outline-hidden ring-ring transition-colors hover:bg-foreground/[0.07] focus-visible:ring-2 data-[active]:bg-foreground/[0.09]"
            >
              <OrgLogo org={activeOrg ?? null} surface="frame" className="size-4.5 shrink-0" />
              <span className="truncate">{activeOrg?.name ?? "Choose a company"}</span>
              <ChevronsUpDownIcon aria-hidden className="size-3 shrink-0 text-sidebar-muted-foreground" />
            </MenuTrigger>
            <MenuPopup side="bottom" align="start" sideOffset={6} className="w-64 rounded-xl">
              {/* One popup, two views. "Switch company" slides the list in over
                  the menu in place — a click, never a hover flyout — and the
                  back row slides it away again. Keyed so each view replays its
                  entrance; rows ease in one after another (index.css). */}
              {view === "menu" ? (
                <div key="menu" data-shell-menu-view="menu" className="shell-menu-view">
                  {activeOrg ? (
                    <>
                      <MenuItem className={COMPANY_MENU_ROW} onClick={() => goToSettings("organization")}>
                        <Building2Icon aria-hidden className={COMPANY_MENU_ICON} />
                        Company settings
                      </MenuItem>
                      <MenuItem className={COMPANY_MENU_ROW} onClick={() => goToSettings("members")}>
                        <UserPlusIcon aria-hidden className={COMPANY_MENU_ICON} />
                        Invite and manage members
                      </MenuItem>
                      <MenuItem className={COMPANY_MENU_ROW} onClick={() => goToSettings("branding")}>
                        <PaletteIcon aria-hidden className={COMPANY_MENU_ICON} />
                        Branding
                      </MenuItem>
                      <MenuSeparator />
                    </>
                  ) : null}
                  <MenuItem
                    className={COMPANY_MENU_ROW}
                    closeOnClick={false}
                    onClick={() => setView("companies")}
                  >
                    <ArrowLeftRightIcon aria-hidden className={COMPANY_MENU_ICON} />
                    Switch company
                    <ChevronRightIcon aria-hidden className="ml-auto size-3.5 text-muted-foreground" />
                  </MenuItem>
                </div>
              ) : (
                <div key="companies" data-shell-menu-view="companies" className="shell-menu-view">
                  <MenuItem
                    className={cn(COMPANY_MENU_ROW, "text-muted-foreground")}
                    closeOnClick={false}
                    onClick={() => setView("menu")}
                  >
                    <ChevronLeftIcon aria-hidden className={COMPANY_MENU_ICON} />
                    Switch company
                  </MenuItem>
                  <MenuSeparator />
                  {/* Vertical only: the separator before dedicated-deployment
                      companies reaches 4px past each edge (-mx-1). */}
                  <div className="max-h-[min(22rem,60dvh)] overflow-x-hidden overflow-y-auto">
                    <OrgMenuItems
                      orgs={orgs}
                      activeOrgId={activeOrg?.id ?? null}
                      onSelect={selectOrg}
                    />
                  </div>
                </div>
              )}
            </MenuPopup>
          </Menu>
          <AppBreadcrumb />
        </>
      ) : null}
    </div>
  );
}

/**
 * The strip's controls are the secondary Button — filled, no border, no
 * shadow. Its stock fill is the same shade as the window in light mode, so on
 * the strip the fill is a tint of the text colour instead, which reads on the
 * window surface of any theme.
 */
const STRIP_CONTROL_CLASS =
  "bg-foreground/[0.055] [:hover,[data-pressed]]:bg-foreground/[0.09] [:active,[data-pressed]]:bg-foreground/[0.11]";

/** The same shortcut chip the sidebar rows wear. */
const SHORTCUT_CHIP_CLASS =
  "h-4 min-w-0 shrink-0 rounded-sm bg-sidebar-control-surface px-1.5 text-[10px] text-sidebar-muted-foreground ring-1 ring-sidebar-border";

/**
 * Workspace places with a shortcut: Plugins & Skills and Automations. Bound
 * here because the strip is mounted on every page, so the keys work from chat,
 * governance, inbox and settings alike. The rows in the chat sidebar show the
 * same bindings as chips.
 */
function useWorkspacePlaceShortcuts() {
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const navigate = useNavigate();
  const activeOrgId = useActiveOrgId();
  const orgSegment = activeOrgId ? orgUrlSegment(activeOrgId) : null;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.target instanceof HTMLElement && event.target.closest("[data-keybinding-capture]")) {
        return;
      }
      const command = resolveShortcutCommand(event, keybindings, {
        context: { terminalFocus: isTerminalFocused() },
      });
      if (command !== "plugins.open" && command !== "automations.open") return;
      event.preventDefault();
      event.stopPropagation();
      const page = command === "plugins.open" ? "connections" : "automations";
      // Without a company the org-less path redirects to the last-picked one.
      void navigate(
        (orgSegment
          ? { to: `/$org/${page}`, params: { org: orgSegment } }
          : { to: `/${page}` }) as never,
      );
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [keybindings, navigate, orgSegment]);
}

/**
 * The strip above the workspace inset: the window's own chrome, outside the
 * card, the way Linear keeps its switcher and search above the content. It
 * sits on the rail's surface, starts where the rail ends, and only exists from
 * the md breakpoint (below it the rail is hidden and the inset fills the
 * screen, so the sidebar header carries the brand there).
 */
export function ShellTopBar() {
  useWorkspacePlaceShortcuts();
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const shortcut = shortcutLabelForCommand(keybindings, "commandPalette.toggle");
  return (
    <div
      data-shell-topbar=""
      data-app-sidebar=""
      className="pointer-events-none fixed left-[var(--nav-rail-width,0px)] right-0 top-0 z-20 hidden h-[var(--shell-top)] items-center gap-3 pr-[var(--shell-gap)] md:flex"
    >
      <ShellBreadcrumb />
      {/* Wide, the field is centred on the strip, not on the space the
          breadcrumb leaves. Once the window compresses it (below 72rem, where
          100vw - 40rem drops under its 32rem), it docks on the right beside
          Ask AI, clear of the breadcrumb. */}
      <div className="pointer-events-none ml-auto flex min-[72rem]:absolute min-[72rem]:inset-x-0 min-[72rem]:ml-0 min-[72rem]:justify-center min-[72rem]:px-[var(--shell-gap)]">
        {/* The one search in the chrome (the sidebar's row is mobile-only
            now), so it is a full control — the standard outline Button, wide,
            with its shortcut shown — not a faint hint. Secondary: filled,
            with no border and no shadow, so it sits flat on the window. */}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => openCommandPalette()}
          aria-label="Search"
          className={cn(
            // Width follows the window: as wide as the strip allows between
            // the breadcrumb and Ask AI, from 8rem up to 32rem. The shortcut
            // drops out when the field gets too narrow to hold it.
            "@container pointer-events-auto w-[clamp(8rem,calc(100vw-40rem),32rem)] cursor-pointer justify-start gap-2 font-normal text-muted-foreground transition-[width] duration-200 ease-out [-webkit-app-region:no-drag]",
            STRIP_CONTROL_CLASS,
          )}
        >
          <SearchIcon aria-hidden />
          <span className="truncate">Search or jump to…</span>
          {shortcut ? (
            <Kbd className={cn(SHORTCUT_CHIP_CLASS, "ml-auto hidden @[15rem]:inline-flex")}>{shortcut}</Kbd>
          ) : null}
        </Button>
      </div>
      <AskAiPanel triggerClassName={cn("pointer-events-auto shrink-0 min-[72rem]:ml-auto [-webkit-app-region:no-drag]", STRIP_CONTROL_CLASS)} />
      <WorkspaceUserMenu placement="strip" />
    </div>
  );
}
